import { Injectable } from '@angular/core';
import { JobTypes } from '@cdw-ae/common-data';
import {
  type ActionLabels,
  type BulkActionLabelsProvider,
  DEFAULT_ACTION_LABELS,
} from '@cdw-ae/common-ui-components-bulk-update-action';
import { type DeviceAction, isDeviceAction } from '../../../layouts/internal/components/device-search/configs/bulk-edit-config';

const SHARED_COMPLETED_SKIPPED = '<strong>{Z} device(s)</strong> were skipped.';

/**
 * EDUS-2163 Phase 6 — Chrome / Google device-action vocabulary that used to
 * live inside `libs/common-ui-components-bulk-update-action/`. Hosted here so
 * the drawer library stays vendor-agnostic; SPOG (and any future host) wires
 * its own copy + UI shape via {@link BULK_ACTION_LABELS_PROVIDER}.
 *
 * Verbatim Figma copy — see plan §"Verified per-action drawer shape".
 */
const CHROME_ACTION_LABELS: Record<DeviceAction, ActionLabels> = {
  enable: {
    drawerTitle: 'Bulk Enable Devices',
    steps: ['Confirm Enable', 'Summary', 'Progress', 'Completed'],
    progressTitle: 'Bulk enable device(s) in progress',
    summaryPendingTemplate:
      'This action will update <strong>{Y} device(s)</strong> with the following changes.',
    completedTitleTemplate: '<strong>{Y} device(s)</strong> were successfully enabled.',
    completedSkippedTemplate: SHARED_COMPLETED_SKIPPED,
    kind: 'status',
    jobType: JobTypes.ENABLE,
    confirmArchetype: 'prose',
    confirmProseCopy:
      '<strong>{Y} device(s)</strong> have been selected for modification. Enabled devices will be able to sign in and sync policies.',
    summaryActionButtonLabel: 'Enable',
  },
  disable: {
    drawerTitle: 'Bulk Disable Devices',
    steps: ['Confirm Disable', 'Summary', 'Progress', 'Completed'],
    progressTitle: 'Bulk disable device(s) in progress',
    summaryPendingTemplate:
      'This action will update <strong>{Y} device(s)</strong> with the following changes.',
    completedTitleTemplate: '<strong>{Y} device(s)</strong> were successfully disabled.',
    completedSkippedTemplate: SHARED_COMPLETED_SKIPPED,
    kind: 'status',
    jobType: JobTypes.DISABLE,
    confirmArchetype: 'prose',
    confirmProseCopy:
      '<strong>{Y} device(s)</strong> have been selected for modification. Disable a device if it is lost or stolen, or before deprovisioning.<br><br>Disabled devices remain enrolled in your domain.',
    summaryActionButtonLabel: 'Disable',
  },
  deprovision: {
    drawerTitle: 'Bulk Deprovision Devices',
    // 5-step Figma flow: Acknowledge + Reason are separate stepper rows. The
    // drawer renders these two steps as a special-case (deprovision-only)
    // until the schema grows multi-step Confirm support.
    steps: [
      'Deprovision Device Acknowledgment',
      'Specify deprovision reason',
      'Summary',
      'Progress',
      'Completed',
    ],
    progressTitle: 'Bulk deprovision device(s) in progress',
    summaryPendingTemplate:
      'This action will update <strong>{Y} device(s)</strong> with the following changes.',
    completedTitleTemplate: '<strong>{Y} device(s)</strong> were successfully deprovisioned.',
    completedSkippedTemplate: SHARED_COMPLETED_SKIPPED,
    kind: 'status',
    jobType: JobTypes.DEPROVISION,
    // confirmArchetype not consulted for deprovision — the drawer renders
    // its dedicated Acknowledge + Reason steps instead.
    confirmArchetype: 'none',
    summaryActionButtonLabel: 'Deprovision',
  },
  'profile-wipe': {
    drawerTitle: 'Bulk Profile Wipe Devices',
    steps: ['Confirm Profile Wipe', 'Summary', 'Queue Job', 'Profile Wipe Queued'],
    progressTitle: 'Bulk profile wipe in progress',
    summaryPendingTemplate: 'This action will queue <strong>{Y} item(s)</strong> for a profile wipe.',
    completedTitleTemplate:
      '<strong>{Y} device(s)</strong> have successfully been queued for a profile wipe. This action will be completed once the device(s) are online to receive the command.',
    completedSkippedTemplate: SHARED_COMPLETED_SKIPPED,
    kind: 'command',
    jobType: JobTypes.WIPE_USERS,
    command: 'WIPE_USERS',
    confirmArchetype: 'warning',
    warningBandCopy:
      '<strong>You are about to queue wipe user profiles from {Y} device(s). This action will remove all user data and cannot be undone.</strong>',
    confirmProseCopy:
      'This action will be queued and will complete once the device(s) are online to receive the command. <strong>Once queued, this action cannot be canceled.</strong>',
    summaryActionButtonLabel: 'Queue Wipe',
  },
  'factory-reset': {
    drawerTitle: 'Bulk Factory Reset Devices',
    steps: ['Factory Reset Acknowledgment', 'Summary', 'Queue Job', 'Factory Reset Queued'],
    progressTitle: 'Bulk factory reset in progress',
    summaryPendingTemplate: 'This action will queue <strong>{Y} device(s)</strong> for a factory reset.',
    completedTitleTemplate:
      '<strong>{Y} device(s)</strong> have successfully been queued for a factory reset. This action will be completed once the device(s) are online to receive the command.',
    completedSkippedTemplate: SHARED_COMPLETED_SKIPPED,
    kind: 'command',
    jobType: JobTypes.FACTORY_RESET,
    command: 'REMOTE_POWERWASH',
    confirmArchetype: 'warning-acknowledge',
    warningBandCopy:
      '<strong>You are about to queue a factory reset for {Y} device(s). This action will erase ALL data on the device(s), and cannot be undone.</strong>',
    confirmProseCopy:
      'This action will be queued and will complete once the device(s) are online to receive the command. <strong>Once queued, this action cannot be canceled.</strong>',
    acknowledgeLabel: 'I acknowledge that I understand this action.',
    summaryActionButtonLabel: 'Queue Factory Reset',
  },
  'move-ou': {
    drawerTitle: 'Bulk Move Devices',
    steps: ['Field Selection', 'Summary', 'Progress', 'Completed'],
    progressTitle: 'Bulk move OU in progress',
    summaryPendingTemplate:
      'This action will move <strong>{Y} device(s)</strong> to the selected organizational unit.',
    completedTitleTemplate: '<strong>{Y} device(s)</strong> were successfully moved.',
    completedSkippedTemplate: SHARED_COMPLETED_SKIPPED,
    kind: 'field',
    jobType: JobTypes.MOVE_OU,
    confirmArchetype: 'none',
    summaryActionButtonLabel: 'Move OU',
  },
  'edit-import': {
    // EDUS-2543 — drawer title carries the selected device count via the
    // {deviceCount} named token, substituted by the drawer's drawerTitleLine.
    drawerTitle: 'Bulk edit - {deviceCount} device(s)',
    steps: ['Field Selection', 'Summary', 'Progress', 'Completed'],
    progressTitle: 'Bulk edit in progress',
    summaryPendingTemplate:
      'This action will update <strong>{Y} device(s)</strong> with the following changes.',
    completedTitleTemplate: '<strong>{Y} device(s)</strong> were successfully updated.',
    completedSkippedTemplate: SHARED_COMPLETED_SKIPPED,
    kind: 'field',
    jobType: JobTypes.BULK_EDIT,
    confirmArchetype: 'none',
  },
};

@Injectable({ providedIn: 'root' })
export class DeviceActionLabelsProvider implements BulkActionLabelsProvider {
  getLabels(actionType: string | undefined): ActionLabels {
    if (!(actionType && isDeviceAction(actionType))) return DEFAULT_ACTION_LABELS;
    return CHROME_ACTION_LABELS[actionType];
  }

  formatLabel(
    template: string,
    counts: { qualified?: number; skipped?: number },
  ): string {
    let out = template;
    if (counts.qualified !== undefined) {
      out = out.replace(/\{Y\}/g, String(counts.qualified));
    }
    if (counts.skipped !== undefined) {
      out = out.replace(/\{Z\}/g, String(counts.skipped));
    }
    return out;
  }
}
